import i18next from 'i18next';
import I18nextBrowserLanguageDetector from 'i18next-browser-languagedetector';
import I18NextHttpBackend from 'i18next-http-backend';
import {
  initReactI18next,
  useTranslation as useTranslationI18next,
} from 'react-i18next';
import { parseHtml } from '.';

/**
 * Initializes the i18next translation framework.
 *
 * NOTE: react-i18next which is used in the setup uses the React Suspense API.
 * You will either need to supply a Suspense component or override the default config
 * value 'react.useSuspense: false'.
 *
 * @param {*} [configOverride] Optionally override i18next init config values.
 */
export function initTranslations(configOverride) {
  return i18next
    .use(I18nextBrowserLanguageDetector)
    .use(I18NextHttpBackend)
    .use(initReactI18next)
    .init({
      ns: process.env.TRANSLATION_NAMESPACES,
      defaultNs: process.env.TRANSLATION_DEFAULT_NS,
      supportedLngs: process.env.TRANSLATION_LANGUAGES,
      nonExplicitSupportedLngs: true,
      load: 'languageOnly',
      cleanCode: true,
      backend: {
        loadPath: process.env.TRANSLATION_PATH,
      },
      ...configOverride,
    });
}

/**
 * Overrides the default useTranslation hook, to add the
 * html-react-parser functionality.
 *
 * Specify `parseHtml: true` in the options of the `t` function to use
 * it.
 *
 * @param {string} [ns] The namespace which should be used. (If missing the default ns will be used)
 * @param {import('react-i18next').UseTranslationOptions} [options] Translation options.
 * @returns {import('react-i18next').UseTranslationResponse}
 *
 * @example
 * const { t } = useTranslation('default');
 *
 * return (
 *  <>
 *    {t('example-key', { parseHtml: true })}
 *  </>
 * );
 */
export function useTranslation(ns, options = {}) {
  const translationFunctions = useTranslationI18next(ns, options);
  const origTranslation = translationFunctions.t;

  const extendedTranslation = (key, options = {}) => {
    if (options.parseHtml) {
      return parseHtml(origTranslation(key, options));
    } else {
      return origTranslation(key, options);
    }
  };

  translationFunctions.t = extendedTranslation;
  translationFunctions[0] = extendedTranslation;

  return translationFunctions;
}

export { i18next };
